/**
 * View Creation and Management Tools
 * 
 * Create and configure all view types:
 * - Floor Plans, Ceiling Plans, Structural Plans
 * - Sections, Elevations, Callouts
 * - 3D Views, Camera Views, Walkthroughs
 * - Schedules, Legends, Drafting Views
 */

import { v4 as uuidv4 } from 'uuid';
import { View, ViewType, Level } from '../core/types';

export interface ViewCreationOptions {
    name: string;
    type: ViewType;
    scale: number;
    detailLevel: 'Coarse' | 'Medium' | 'Fine';
    discipline?: 'Architectural' | 'Structural' | 'Mechanical' | 'Electrical' | 'Plumbing' | 'Coordination';
}

/**
 * Create a Floor Plan view
 */
export function createFloorPlanView(
    level: Level,
    options: Partial<ViewCreationOptions> = {}
): View {
    return {
        id: uuidv4(),
        name: options.name || level.name,
        type: 'FloorPlan',
        scale: options.scale || 100,
        detailLevel: options.detailLevel || 'Coarse',
        visibility: getDefaultVisibility('FloorPlan', options.discipline),
        filters: [],
        viewRange: {
            top: 4.0,
            cutPlane: 1.2,
            bottom: 0,
            depth: 1.0
        },
        camera: {
            position: [0, 0, level.elevation + 50],
            target: [0, 0, level.elevation],
            projection: 'Orthographic'
        },
        cutPlane: {
            origin: [0, 0, level.elevation + 1.2], // 1.2m above floor
            normal: [0, 0, -1]
        }
    };
}

/**
 * Create a Section view
 */
export function createSectionView(
    name: string,
    origin: [number, number, number],
    direction: [number, number, number],
    options: Partial<ViewCreationOptions> = {}
): View {
    return {
        id: uuidv4(),
        name: name,
        type: 'Section',
        scale: options.scale || 50,
        detailLevel: options.detailLevel || 'Medium',
        visibility: getDefaultVisibility('Section'),
        filters: [],
        camera: {
            position: [origin[0] - direction[0] * 10, origin[1] - direction[1] * 10, origin[2]],
            target: origin,
            projection: 'Orthographic'
        },
        cutPlane: {
            origin: origin,
            normal: direction
        }
    };
}

/**
 * Create an Elevation view
 */
export function createElevationView(
    name: string,
    direction: 'North' | 'South' | 'East' | 'West',
    options: Partial<ViewCreationOptions> = {}
): View {
    const directionVectors: Record<string, [number, number, number]> = {
        'North': [0, 1, 0],
        'South': [0, -1, 0],
        'East': [1, 0, 0],
        'West': [-1, 0, 0]
    };
    const dir = directionVectors[direction];

    return {
        id: uuidv4(),
        name: name || `${direction} Elevation`,
        type: 'Elevation',
        scale: options.scale || 100,
        detailLevel: options.detailLevel || 'Medium',
        visibility: getDefaultVisibility('Elevation'),
        filters: [],
        camera: {
            position: [dir[0] * -50, dir[1] * -50, 5],
            target: [0, 0, 5],
            projection: 'Orthographic'
        }
    };
}

/**
 * Create a 3D view
 */
export function create3DView(
    name: string,
    perspective: boolean = true,
    options: Partial<ViewCreationOptions> = {}
): View {
    return {
        id: uuidv4(),
        name: name || (perspective ? '{3D}' : 'Default 3D View'),
        type: '3D',
        scale: 1,
        detailLevel: options.detailLevel || 'Medium',
        visibility: getDefaultVisibility('3D'),
        filters: [],
        camera: {
            position: [20, 20, 15],
            target: [0, 0, 3],
            projection: perspective ? 'Perspective' : 'Orthographic'
        }
    };
}

/**
 * Create a Ceiling Plan view
 */
export function createCeilingPlanView(
    level: Level,
    options: Partial<ViewCreationOptions> = {}
): View {
    return {
        id: uuidv4(),
        name: options.name || `${level.name} RCP`,
        type: 'CeilingPlan',
        scale: options.scale || 100,
        detailLevel: options.detailLevel || 'Coarse',
        visibility: getDefaultVisibility('CeilingPlan'),
        filters: [],
        camera: {
            position: [0, 0, level.elevation - 50],
            target: [0, 0, level.elevation + 2.4], // Looking at ceiling
            projection: 'Orthographic'
        },
        cutPlane: {
            origin: [0, 0, level.elevation + 2.4],
            normal: [0, 0, 1]
        }
    };
}

/**
 * Create a Drafting View
 */
export function createDraftingView(
    name: string,
    scale: number = 20
): View {
    return {
        id: uuidv4(),
        name: name,
        type: 'Drafting',
        scale: scale,
        detailLevel: 'Fine',
        visibility: {},
        filters: []
    };
}

/**
 * Create a Legend View
 */
export function createLegendView(
    name: string
): View {
    return {
        id: uuidv4(),
        name: name,
        type: 'Legend',
        scale: 1,
        detailLevel: 'Fine',
        visibility: {},
        filters: []
    };
}

/**
 * Get default visibility settings based on view type and discipline
 */
function getDefaultVisibility(
    _viewType: ViewType,
    discipline?: string
): Record<string, boolean> {
    const base: Record<string, boolean> = {
        'Walls': true,
        'Doors': true,
        'Windows': true,
        'Floors': true,
        'Roofs': true,
        'Structural Columns': true,
        'Structural Framing': true,
        'Furniture': true,
        'Plumbing Fixtures': true,
        'Mechanical Equipment': true,
        'Electrical Equipment': true,
        'Ducts': true,
        'Pipes': true,
        'Conduits': true,
        'Ceilings': true,
        'Rooms': true,
        'Grids': true,
        'Levels': false, // Usually off in plan views
    };

    // Adjust based on discipline
    if (discipline === 'Structural') {
        base['Furniture'] = false;
        base['Plumbing Fixtures'] = false;
        base['Mechanical Equipment'] = false;
        base['Electrical Equipment'] = false;
    } else if (discipline === 'Mechanical') {
        base['Electrical Equipment'] = false;
        base['Pipes'] = false;
        base['Conduits'] = false;
    }

    return base;
}

/**
 * Apply view template
 */
export function applyViewTemplate(
    view: View,
    template: Partial<View>
): View {
    return {
        ...view,
        ...template,
        id: view.id, // Keep original ID
        name: view.name // Keep original name
    };
}
